import React, { useState } from 'react';
import { Check, ChevronRight, Download, ExternalLink, Trash2 } from 'lucide-react';
import RefLink from '../shared/RefLink';
import FileList from '../shared/FileList';
import { workflowRefHref } from '../shared/WorkflowRefLink';
import { formatDate } from '../../utils/dateFormat';
import { latestAddendumCard, latestAddendumChip } from '../workflow/constants';
import { nowLabel } from '../shared/NowName';
import RiskChangesList from './RiskChangesList';
import { engagementTag } from '../../utils/engagementName';
import {
  NATURE_OPTIONS, IMPACT_OPTIONS, PURPOSE_TEXT, CONCLUSION_TEXT, natureLabel, titleFor, eqrLabel,
} from '../../pages/qcAddendum';

/**
 * Newest first — the same ruler the server lists with (approval date desc,
 * no-date rows last, then id desc). Every surface that stacks AddendumRows
 * sorts through here, so index 0 is always the one `latest` highlights.
 */
export const sortAddendaNewestFirst = (list) => [...(list || [])].sort((a, b) => {
  const t = (x) => (x.approvedOn ? new Date(x.approvedOn).getTime() : -1);
  return (t(b) - t(a)) || (b.id - a.id);
});

/**
 * One addendum, read-only, wherever addenda are listed (the engagement page's
 * QC block, the QC record's Addenda section). Collapsed = one line (title ·
 * date · risk move · source) with a Download PDF button; expanded = the
 * template's rows, so the manager reads the addendum HERE without opening
 * the request that produced it.
 *
 *   pdfHref    where this surface downloads the PDF (its own gate)
 *   fileHref   (file) => url for the addendum's supporting files
 *   record     the parent QC record (for the header rows)
 *   onDelete   Management only, manual addenda only
 *   latest     newest of the stack (index 0 after sortAddendaNewestFirst) —
 *              tinted card + "Latest" chip
 */
const Row = ({ label, children }) => (
  <tr>
    <th scope="row" className="w-44 px-3 py-1.5 text-left text-xs font-semibold text-slate-600 bg-slate-50 border border-slate-200 align-top">{label}</th>
    <td className="px-3 py-1.5 text-sm text-slate-800 border border-slate-200 whitespace-pre-line break-words">{children}</td>
  </tr>
);

/** A filled box with a tick = chosen; an empty box = one of the template's
 *  options that was not chosen. */
const Tick = ({ on, children }) => (
  <div className={`flex items-start gap-2 ${on ? 'text-slate-900 font-medium' : 'text-slate-400'}`}>
    <span className={`mt-0.5 shrink-0 w-4 h-4 rounded-sm border flex items-center justify-center ${on ? 'border-slate-800 bg-slate-800' : 'border-slate-300 bg-white'}`}>
      {on && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
    </span>
    <span>{children}</span>
  </div>
);

/**
 * The addendum's "Client Name" lines, one per covered engagement: the record's
 * own name snapshot, "(now: …)" when the client master moved on, and the
 * engagement tag (engagementTag) when the record covers several engagements or
 * the engagement is one of a multi-per-FYE service — two engagements of one
 * company stay apart.
 */
export const addendumClientNames = (engagements = []) => (engagements || []).map((e) => {
  const name = nowLabel(e.entityName || e.engagementName, e.entityNameNow);
  const tagged = e.entityName && (engagements.length > 1 || e.engagementSeq || e.serviceDescription);
  const tag = tagged ? engagementTag(e.serviceProvided, e.engagementSeq, e.serviceDescription) : '';
  return name && tag ? `${name} (${tag})` : name;
}).filter(Boolean);

const AddendumRow = ({ addendum: a, record, pdfHref, fileHref = null, onDelete = null, defaultOpen = false, latest = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const qcType = record?.qcType;
  const title = titleFor(qcType);
  const requestHref = a.sourceRequestNo ? workflowRefHref(a.sourceRequestNo) : null;
  const primary = ((record && record.engagements) || [])[0] || {};
  const clientNames = addendumClientNames(record && record.engagements);

  return (
    <div className={`border rounded-lg ${latest ? latestAddendumCard : 'border-slate-200 bg-white'}`}>
      <div className="flex items-center gap-2 px-3 py-2">
        <button type="button" onClick={() => setOpen((v) => !v)} className="flex-1 min-w-0 flex items-start gap-2 text-left">
          <ChevronRight className={`w-4 h-4 mt-0.5 text-slate-400 shrink-0 transition-transform ${open ? 'rotate-90' : ''}`} />
          <span className="min-w-0">
            <span className="flex flex-wrap items-center gap-1.5 text-sm font-medium text-slate-900">
              <span>{title} · {a.approvedOn ? formatDate(a.approvedOn) : 'no date'}</span>
              {latest && <span className={latestAddendumChip}>Latest</span>}
            </span>
            <span className="block text-xs text-slate-500">
              {(a.nature || []).map(natureLabel).join(', ')}
              {a.riskChanged
                ? ((a.riskChanges || []).length > 0
                  ? <> · Risk revised for <span className="font-medium text-amber-700">{a.riskChanges.length} engagement{a.riskChanges.length > 1 ? 's' : ''}</span></>
                  : <> · Risk <span className="font-medium text-amber-700">{a.riskFrom || '—'} → {a.riskTo}</span></>)
                : ' · Risk unchanged'}
              {a.eqrChanged && <> · EQR <span className="font-medium text-amber-700">{eqrLabel(a.eqrFrom)} → {eqrLabel(a.eqrTo)}</span></>}
              {' · '}
              {a.sourceRequestNo ? `via ${a.sourceRequestNo}` : 'recorded in Management'}
            </span>
          </span>
        </button>
        <a href={pdfHref} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-300 text-xs font-medium text-slate-700 hover:bg-slate-50 shrink-0">
          <Download className="w-3.5 h-3.5" /> Download PDF
        </a>
        {onDelete && (
          <button type="button" onClick={onDelete} className="text-slate-400 hover:text-red-600 shrink-0" title="Delete this manually recorded addendum">
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      {open && (
        <div className="px-3 pb-3 space-y-3">
          <table className="w-full border-collapse">
            <tbody>
              <Row label="Firm's Name">{a.firmName || '—'}</Row>
              <Row label="Client Name">{clientNames.length ? clientNames.join('\n') : (record?.entityName || '—')}</Row>
              <Row label="Engagement Type">{primary.serviceProvided || '—'}</Row>
              <Row label="Period End">{formatDate(primary.yearEnd || record?.yearEnd) || '—'}</Row>
              <Row label="QC Form Reference">
                {qcType}{record?.legacyImport ? (record.signedFormPending ? ' (signed form pending)' : ' (signed form uploaded)') : ''}
              </Row>
              <Row label="Date of Original Approval">{record?.approvedOn ? formatDate(record.approvedOn) : '—'}</Row>
              <Row label="Purpose of Addendum"><span className="text-slate-600">{PURPOSE_TEXT}</span></Row>
              <Row label="Nature of Update / Change">
                <div className="space-y-0.5">
                  {NATURE_OPTIONS.map(([code, label]) => (
                    <Tick key={code} on={(a.nature || []).includes(code)}>
                      {label}{code === 'other' && (a.nature || []).includes('other') && a.natureOther ? `: ${a.natureOther}` : ''}
                    </Tick>
                  ))}
                </div>
              </Row>
              <Row label="Details of Update">{a.details || '—'}</Row>
              <Row label="Impact Assessment">
                <div className="space-y-2">
                  <div>
                    <div className="text-xs font-semibold text-slate-500 mb-0.5">Risk Rating</div>
                    <Tick on={!a.riskChanged}>Unchanged</Tick>
                    {(a.riskChanges || []).length > 0 ? (
                      <>
                        <Tick on>Revised — per engagement</Tick>
                        <div className="pl-5 mt-1"><RiskChangesList changes={a.riskChanges} /></div>
                      </>
                    ) : (
                      <Tick on={!!a.riskChanged}>Revised from {a.riskChanged ? (a.riskFrom || '—') : '____'} to {a.riskChanged ? (a.riskTo || '—') : '____'}</Tick>
                    )}
                  </div>
                  {a.riskChanged && (
                    <div>
                      <div className="text-xs font-semibold text-slate-500 mb-0.5">Engagement Quality Reviewer</div>
                      {a.eqrChanged
                        ? <div className="text-slate-900 font-medium">{eqrLabel(a.eqrFrom)} → {eqrLabel(a.eqrTo)}</div>
                        : <div className="text-slate-600">Unchanged — {eqrLabel(a.eqrFrom)}</div>}
                    </div>
                  )}
                  <div>
                    <div className="text-xs font-semibold text-slate-500 mb-0.5">Independence / Ethical Considerations</div>
                    {IMPACT_OPTIONS.map(([code, label]) => <Tick key={code} on={a.independenceImpact === code}>{label}</Tick>)}
                  </div>
                </div>
              </Row>
              <Row label="Conclusion"><span className="text-slate-600">{CONCLUSION_TEXT}</span></Row>
              <Row label="Source">
                {a.sourceRequestNo && requestHref ? (
                  <RefLink to={requestHref} className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800">
                    <ExternalLink className="w-3.5 h-3.5" /> Approved through {a.sourceRequestNo}
                  </RefLink>
                ) : 'Recorded in Management (signed outside the system)'}
              </Row>
            </tbody>
          </table>
          {fileHref && (
            <FileList
              files={a.files || []}
              row={(f) => ({ name: f.originalName, href: fileHref(f), pad: 'py-1.5' })}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default AddendumRow;
